import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Alert, Card, ProgressBar } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../api/api';
import './ResetPassword.css';

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  // Calculate password strength
  useEffect(() => {
    let strength = 0;
    if (newPassword.length >= 6) strength += 20;
    if (newPassword.length >= 10) strength += 20;
    if (/[A-Z]/.test(newPassword)) strength += 20;
    if (/[0-9]/.test(newPassword)) strength += 20;
    if (/[!@#$%^&*]/.test(newPassword)) strength += 20;
    setPasswordStrength(strength);
  }, [newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);

    if (!newPassword || !confirmPassword) {
      setError('Please enter and confirm your new password');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.resetPassword(token, newPassword, confirmPassword);
      if (response.data.success) {
        setMessage(response.data.message);
        setSubmitted(true);
        setNewPassword('');
        setConfirmPassword('');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 40) return 'danger';
    if (passwordStrength < 70) return 'warning';
    return 'success';
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength < 40) return 'Weak';
    if (passwordStrength < 70) return 'Fair';
    return 'Strong';
  };

  if (error && error.includes('Invalid reset link')) {
    return (
      <div className="reset-password-container">
        <Container className="d-flex justify-content-center align-items-center min-vh-100">
          <Card className="reset-password-card shadow-lg">
            <Card.Header className="bg-danger text-white text-center py-4">
              <h2>Invalid Reset Link</h2>
            </Card.Header>
            <Card.Body className="p-5">
              <Alert variant="danger" className="mb-4">
                <p className="mb-0">{error}</p>
              </Alert>
              <div className="text-center">
                <Button variant="primary" onClick={() => navigate('/login')} className="me-2">
                  Back to Login
                </Button>
                <Button variant="outline-primary" onClick={() => navigate('/forgot-password')}>
                  Request New Link
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Card className="reset-password-card shadow-lg">
          <Card.Header className="bg-success text-white text-center py-4">
            <h2>Create New Password</h2>
          </Card.Header>
          <Card.Body className="p-5">
            {submitted ? (
              <>
                <Alert variant="success" className="mb-4">
                  <h4>Password Reset Successfully!</h4>
                  <p>
                    Your password has been reset. You can now login with your new password.
                  </p>
                  <p className="mb-0 text-muted small">
                    Redirecting to login page in 3 seconds...
                  </p>
                </Alert>
                <div className="text-center">
                  <Button variant="primary" onClick={() => navigate('/login')}>
                    Go to Login Now
                  </Button>
                </div>
              </>
            ) : (
              <>
                {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
                {message && <Alert variant="info" className="mb-4">{message}</Alert>}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={loading}
                    />
                    <Form.Text className="text-muted d-block mt-2">
                      Minimum 6 characters. Include uppercase, numbers, and symbols for better security.
                    </Form.Text>
                  </Form.Group>

                  {newPassword && (
                    <Form.Group className="mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <small>Password Strength:</small>
                        <small className={`text-${getPasswordStrengthColor()}`}>
                          {getPasswordStrengthLabel()}
                        </small>
                      </div>
                      <ProgressBar
                        now={passwordStrength}
                        variant={getPasswordStrengthColor()}
                        className="mt-2"
                      />
                    </Form.Group>
                  )}

                  <Form.Group className="mb-4">
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading}
                      isInvalid={confirmPassword && newPassword !== confirmPassword}
                    />
                    {confirmPassword && newPassword !== confirmPassword && (
                      <Form.Control.Feedback type="invalid" className="d-block">
                        Passwords do not match
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                  <Button
                    variant="success"
                    type="submit"
                    className="w-100 mb-3"
                    disabled={loading || !newPassword || !confirmPassword}
                  >
                    {loading ? 'Resetting Password...' : 'Reset Password'}
                  </Button>
                </Form>

                <hr className="my-4" />

                <div className="text-center">
                  <p className="text-muted">
                    Remember your password? <a onClick={() => navigate('/login')} style={{ cursor: 'pointer' }}>Back to login</a>
                  </p>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default ResetPassword;
